import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {
  Box,
  Button,
  CardMedia,
  Typography,
  FormHelperText,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  black,
  blueGrayText,
  gray,
  primarycolor
} from '../../constants/Colors';
import { formLabelWrapper, formWideMb } from '../../constants/Location';

import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

const styles = {
  bgColor: {
    bgcolor: '#DBDBE0'
  },
  formLabelWrapper,
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  imageIcon: {
    bgcolor: blueGrayText,
    height: '40px',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40px'
  },
  imagePersonIcon: {
    '@media screen and (max-width:480px)': {
      left: '10%'
    },
    bgcolor: blueGrayText,
    height: '40px',
    left: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40px'
  },
  label: {
    display: 'block',
    fontSize: '12px',
    mb: '8px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  uploadButton: {
    '&.Mui-disabled': {
      color: '#FFF'
    },
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    boxShadow: 'none',
    height: '36px',
    minWidth: '157px',
    width: '157px'
  }
};

interface Props {
  type: string;
  preview: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  requiredText?: string;
  isRequired?: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  otherError?: string | null | null;
  disabled?: boolean;
}

const CustomImageUploader = React.forwardRef(
  (props: Props): React.ReactElement | JSX.Element => {
    const {
      type,
      preview,
      onChange,
      requiredText,
      isRequired,
      error,
      otherError,
      helperText,
      disabled
    } = props;
    const { t } = useTranslation();

    let descDirection,
      descRowGap,
      description,
      image,
      title,
      wholeAlign,
      wholeDirection;

    if (type === 'logo') {
      title = t('form.logo');
      description = t('form.logoDescription');
      descRowGap = '16px';
      wholeDirection = 'column';
      wholeAlign = 'flex-start';
      descDirection = 'row-reverse';
      image = {
        borderRadius: '50%',
        height: '64px',
        marginBottom: '10px',
        width: '64px'
      };
    } else if (type === 'bg') {
      title = t('form.bg');
      description = t('form.bgDescription');
      descRowGap = '16px';
      wholeDirection = 'column';
      wholeAlign = 'flex-start';
      descDirection = 'row-reverse';
      image = {
        '@media screen and (max-width:480px)': {
          height: '78px',
          width: '311px'
        },
        height: '100px',
        marginBottom: '10px',
        width: '400px'
      };
    } else if (type === 'icon') {
      title = t('staff.icon');
      description = t('staff.iconDescription');
      descRowGap = '8px';
      wholeDirection = 'row';
      wholeAlign = 'conter';
      descDirection = 'column-reverse';
      image = {
        borderRadius: '50%',
        height: '64px',
        width: '64px'
      };
    } else if (type === 'mediaLogo') {
      title = t('form.mediaLogo');
      description = t('form.mediaLogoDescription');
      descRowGap = '16px';
      wholeDirection = 'column';
      wholeAlign = 'flex-start';
      descDirection = 'row-reverse';
      image = {
        '@media screen and (max-width:480px)': {
          height: '175.5px',
          width: '311px'
        },
        height: '144px',
        marginBottom: '10px',
        width: '256px'
      };
    } else {
      title = t('form.article');
      description = t('form.articleDescription');
      descRowGap = '16px';
      wholeDirection = 'row';
      wholeAlign = 'center';
      descDirection = 'column';
      image = {
        '@media screen and (max-width:480px)': {
          height: '78px',
          width: '100%'
        },
        height: '117px',
        marginBottom: '10px',
        width: '311px'
      };
    }

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <>
        <Typography component="div" sx={{ marginBottom: formWideMb }}>
          <Typography
            align="left"
            sx={[
              styles.formLabelWrapper,
              {
                display: 'flex',
                flexDirection: 'row'
              }
            ]}
            component="div"
          >
            <Typography color={gray} sx={styles.label} component="span">
              {title}
            </Typography>
            {isRequired ? (
              <Typography component="span" sx={styles.required}>
                {requiredText}
              </Typography>
            ) : (
              ''
            )}
          </Typography>
          <Grid
            container
            sx={{
              alignItems: wholeAlign,
              columnGap: '24px',
              flexDirection: wholeDirection,
              rowGap: '16px'
            }}
          >
            {preview ? (
              <CardMedia image={preview} sx={image} />
            ) : (
              <Typography
                component="div"
                sx={{
                  '@media screen and (max-width:480px)': {
                    width: '100%'
                  },
                  position: 'relative'
                }}
              >
                {type === 'bg' || type === 'article' || type === 'mediaLogo' ? (
                  <ImageOutlinedIcon sx={styles.imageIcon} />
                ) : (
                  type === 'icon' && (
                    <PersonOutlineOutlinedIcon sx={styles.imagePersonIcon} />
                  )
                )}
                <Box sx={[image, styles.bgColor]} />
              </Typography>
            )}
            <Typography
              component="div"
              sx={{
                columnGap: '16px',
                display: 'flex',
                flexDirection: {
                  sm: descDirection,
                  xs: 'column'
                },
                rowGap: descRowGap
              }}
            >
              <Button
                component="label"
                variant="contained"
                sx={styles.uploadButton}
                disabled={disabled}
              >
                <input
                  accept=".jpg, .jpeg, .png, .webp, .gif, .svg"
                  type="file"
                  hidden
                  onChange={onChange}
                />
                <AddIcon sx={{ color: '#FFF' }} />
                {t('form.logoUploadButton')}
              </Button>
              <Typography
                sx={[
                  styles.label,
                  {
                    color: black,
                    marginBottom: '4px',
                    whiteSpace: 'pre-wrap'
                  }
                ]}
              >
                {description}
              </Typography>
            </Typography>
          </Grid>
          <FormHelperText sx={styles.helper} error={error}>
            {textfieldHelper()}
          </FormHelperText>
          <FormHelperText sx={styles.helper} error={!null}>
            {otherError}
          </FormHelperText>
        </Typography>
      </>
    );
  }
);

CustomImageUploader.displayName = 'CustomImageUploader';
export default CustomImageUploader;
